<script>
  import { activeProductUrl } from "$lib/utilities/menu-helpers";
  import CallToActionComponent from "$lib/contentTypes/CallToActionComponent.svelte";
  import Menu from "$lib/contentTypes/Menu.svelte";
  import RichText from "$lib/components/RichText.svelte";
  import Heading from "$lib/components/Heading.svelte";
  import NavBrand from "./NavBrand.svelte";
  import { textToId } from "$lib/utilities/dom-helpers";

  export let productRootUrl;
  export let utilityMenu;
  export let primaryMenu;
  export let trailingContent;

  const ctaFields = trailingContent?.children.find(
    (item) => item.contentType === "callToActionComponent"
  ); // flattened
  const socialMenu = trailingContent?.children.find(
    (item) => item.contentType === "menu" && item.fields?.type === "Icon List"
  );
  const legalInfo = trailingContent?.children.find(
    (item) => item.contentType === "freeRichTextComponent"
  ); // flattened

  const utilityMenuText = "Secondary Utility";
  const primaryMenuText = "Secondary Product";
  const utilityMenuLabelId = textToId(utilityMenuText);
  const primaryMenuLabelId = textToId(primaryMenuText);

  const isActiveProduct = activeProductUrl(productRootUrl, utilityMenu.children);
</script>

<hr aria-hidden="true" class="border-neutral-300" />
<footer
  class="static lg:max-w-screen-lg bg-white lg:py-60 pt-32 pb-48 mx-auto lg:px-72"
  data-headings-increment
>
  <Heading text="Footer" class="sr-only" />

  <!-- product list -->
  {#if utilityMenu}
    <div class="mx-auto" data-headings-increment>
      <Heading text={utilityMenuText} class="sr-only" />
      <ul
        class="list-none flex flex-col lg:flex-row items-center gap-x-72"
        aria-labelledby={utilityMenuLabelId}
      >
        {#each utilityMenu.children ?? [] as menuItem}
          {#if menuItem.contentType === "linkComponent" && menuItem.type === "logoLink"}
            <li
              class="w-full lg:w-auto flex justify-center lg:justify-start items-center border-b border-neutral-300 lg:border-none h-108 lg:h-auto"
              active={isActiveProduct(menuItem)}
            >
              <span
                class="flex flex-col justify-between gap-y-8"
                class:opacity-50={!isActiveProduct(menuItem)}
              >
                <NavBrand data={menuItem} />
                <hr
                  class="border-4 h-0 text-highlight rounded-2"
                  class:hidden={!isActiveProduct(menuItem)}
                  aria-hidden="true"
                />
              </span>
            </li>
          {/if}
        {/each}
      </ul>
    </div>
  {/if}

  <!-- product menus -->

  <div class="pt-48 lg:pt-64 mx-0 md:mx-auto" data-headings-increment>
    <Heading text={primaryMenuText} class="sr-only" />
    <ul
      class="list-none flex flex-col lg:flex-row lg:gap-x-72 flex-nowrap lg:gap-y-24"
      aria-labelledby={primaryMenuLabelId}
    >
      {#each primaryMenu?.children ?? [] as menu}
        {#if menu.fields?.type}
          <li>
            <Menu data={menu} />
          </li>
        {/if}
      {/each}
    </ul>
  </div>

  <!-- shared trailing content -->
  <div class="mx-auto pt-48 lg:pt-72 pb-24 px-24 lg:px-0" data-headings-increment>
    <div class="flex flex-col lg:flex-nowrap flex-wrap justify-center gap-x-72 gap-y-32">
      <!-- cta -->
      {#if ctaFields}
        <div class="w-full">
          <CallToActionComponent
            data={{ fields: ctaFields }}
            containerClass="!p-0"
            bodyClass="!p-0 !w-full"
            titleClass="!text-size-16"
            descriptionClass="text-size-14"
          />
        </div>
      {/if}

      <!-- social menu -->
      {#if socialMenu}
        <div class="lg:w-1/2 sm:w-full">
          <Menu data={socialMenu} />
        </div>
      {/if}
    </div>
  </div>

  <!-- legal stuff -->
  {#if legalInfo}
    <div class="px-24 lg:px-0" data-headings-increment data-cy="legal-section">
      <div class="w-full text-size-12">
        <Heading text="Legal Information" class="sr-only" />
        <RichText node={legalInfo.richTextContent} class="!text-size-12" />
      </div>
    </div>
  {/if}
</footer>

<!--Note that forced-colors targets Windows high contrast mode, inverted-colors targets 
the Safari browser, and -ms-high-contrast provides legacy support for IE. 

Also a fun fact: On Mac, Firefox isn't told when a high-contrast theme is in use. If you
want your high-contrast themes to appear correctly in Firefox, you have to change the in-broswer
contrast settings. -->
<style>
  @media (forced-colors: active), (inverted-colors), (-ms-high-contrast: active) {
    span {
      opacity: 1;
    }
  }
</style>
